import { el, KEYED_LIST, resizer } from "@dhx/ts-common/dom";
import { getScrollbarWidth } from "@dhx/ts-common/html";
import { calculatePositions } from "./../helpers/data";
import { isCssSupport } from "./../helpers/main";
import { ICol, ICoords, IGrid, IRendererConfig } from "./../types";
import { getCells, getHandlers, getShifts, getSpans } from "./Cells";
import { getFixedCols } from "./FixedCols";
import { getFixedRows } from "./FixedRows";
import {findIndex} from "../../../ts-common/core";

const BORDERS = 2;

function getRenderConfig(obj: any, data: any[], wrapperSizes: any): IRendererConfig {
	const config = obj.config;
	const positions = calculatePositions(wrapperSizes.width, wrapperSizes.height, obj._scroll, config);
	return {
		...config,
		data,
		scroll: obj._scroll,
		$positions: positions,
		headerHeight: config.$headerLevel * config.headerRowHeight,
		footerHeight: config.$footerLevel * config.footerRowHeight,
		firstColId: config.columns[0].id,
		events: obj.events,
		currentColumns: config.columns.slice(positions.xStart, positions.xEnd),
		sortBy: obj._sortBy,
		sortDir: obj._sortDir
	};
}
function getElementSizes(element: HTMLElement | any): { width: number; height: number } {
	if (!element) {
		return {
			width: 1,
			height: 1
		};
	}
	const styles = element.currentStyle || window.getComputedStyle(element);
	const paddingsByWidth = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight) || 0;
	const paddingsByHeight = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom) || 0;
	return {
		width: element.clientWidth - paddingsByWidth,
		height: element.clientHeight - paddingsByHeight
	};
}
export function render(vm: any, obj: IGrid, data: any[], htmlEvents: any, selection: any, uid: string) {
	if (!(obj as any)._container) {
		obj.config.width = 1;
		obj.config.height = 1;
	}

	// if grid placed inside another component, it will fit to its container
	if (vm && vm.node && vm.node.parent && vm.node.parent.el) {
		const parentNode = vm.node.parent.el;
		const parentSizes = getElementSizes(parentNode);
		obj.config.width = parentSizes.width;
		obj.config.height = parentSizes.height;
	}
	const config = obj.config;
	// when grid is destructing and user try to repaint it
	if (!config) {
		return el("div");
	}

	if (!config.columns.length) {
		return el(".dhx_grid");
	}
	if (!data || !obj.data) {
		data = [];
	}

	if (config.columns.reduce((check, col) => check = !col.hidden ? col.hidden : check, true)) {
		config.$totalHeight = 0;
	} else {
		config.$totalHeight = data.length * config.rowHeight;
	}

	const sizes = getElementSizes((obj as any)._container);
	const wrapperSizes = {
		width: config.width ? config.width : sizes.width,
		height: config.height ? config.height : sizes.height
	};

	// TODO: Remove scroll
	if (config.fitToContainer) {
		const scrollbarY = config.$totalHeight > wrapperSizes.height ? getScrollbarWidth() : 0;
		config.$totalWidth = wrapperSizes.width - BORDERS - scrollbarY;

		const total = config.columns.reduce((width: number, col: ICol) => col.hidden ? width : width + col.width, 0);
		const per = config.$totalWidth / total;
		const mTotal = config.columns.reduce((width: number, col: ICol) => {
			const unchangeable = (col.maxWidth && (col.maxWidth < (col.width * per))) || (col.minWidth && (col.minWidth > (col.width * per)));
			return unchangeable ? width + col.width : width;
		}, 0);
		const newPer = (config.$totalWidth - mTotal) / (total - mTotal);
		config.$totalWidth = 0;
		config.columns.map((col) => {
			const unchangeable = (col.maxWidth && (col.maxWidth < (col.width * newPer))) || (col.minWidth && (col.minWidth > (col.width * newPer)));
			if (!unchangeable) {
				col.width = col.width * newPer;
			} else {
				col.width = col.maxWidth || col.minWidth;
			}
			config.$totalWidth += col.hidden ? 0 : col.width;
		});
	}
	config.width = wrapperSizes.width;
	config.height = wrapperSizes.height;

	const renderConfig = getRenderConfig(obj, data, wrapperSizes);
	renderConfig.selection = selection;
	renderConfig.datacollection = obj.data;
	const shifts = getShifts(renderConfig);

	const isSticky = isCssSupport("position", "sticky");
	const gridBodyHeight = getContentHeight(renderConfig, isSticky, wrapperSizes);

	const layoutState = {
		wrapper: wrapperSizes,
		sticky: isSticky,
		shifts,
		gridBodyHeight
	};

	const header = getFixedRows(renderConfig, { ...layoutState, name: "header", position: "top" });
	const footer = renderConfig.$footer ? getFixedRows(renderConfig, { ...layoutState, name: "footer", position: "bottom" }) : null;

	const lessByWidth = renderConfig.$totalWidth + BORDERS < wrapperSizes.width ? "dhx_grid-less-width" : "";
	const lessByHeight = renderConfig.$totalHeight + BORDERS < wrapperSizes.height ? "dhx_grid-less-height" : "";

	return el(".dhx_grid.dhx_widget", {
		class: (renderConfig.css || "") + (!isSticky ? " dhx_grid_border" : ""),
		dhx_widget_id: uid
	}, [
			resizer(() => obj.paint()),
			el(".dhx_grid-content", {
				style: { ...wrapperSizes },
				onclick: htmlEvents.onclick,
				class: `${lessByWidth} ${lessByHeight}`.trim()
			}, [
					isSticky ? null : header,
					el(".dhx_grid-body", {
						style: {
							height: gridBodyHeight,
							width: wrapperSizes.width - BORDERS,
						},
						onscroll: htmlEvents.onscroll,
						_ref: "grid_body"
					}, [
							isSticky ? header : null,
							getGridData(renderConfig, shifts),
							isSticky ? footer : null
						]),
					getFixedCols(renderConfig, layoutState),
					isSticky ? null : footer
				])
		]);
}

function getGridData(renderConfig: IRendererConfig, shifts: ICoords): any {
	const content = getCells(renderConfig);
	const contentSpans = getSpans(renderConfig);

	let resizedLine;
	if (renderConfig.$resizing) {
		const colIndex = findIndex(renderConfig.columns, col => col.id === renderConfig.$resizing);
		const firstCellLeft = renderConfig.columns.slice(0, colIndex).reduce((total, c) => total += c.width, 0) + renderConfig.columns[colIndex].width;
		resizedLine = el(".dhx_grid-resize-line",
			{
				style: {
					top: 0,
					left: firstCellLeft,
					height: renderConfig.$totalHeight
				}
			}
		);
	}
	let selection = renderConfig.selection ? renderConfig.selection.toHTML() : null;
	selection = typeof selection === "string" ? el("div.dhx_selection", { ".innerHTML": selection }) : selection;

	const pos = renderConfig.$positions;

	return el(".dhx_data-wrap", {
		style: {
			"height": renderConfig.$totalHeight,
			"width": renderConfig.$totalWidth,
			"padding-left": shifts.x,
			"padding-top": shifts.y,
		}
	}, [
			el(".dhx_grid_data", { _flags: KEYED_LIST, ...getHandlers(pos.yStart, pos.xStart, renderConfig) }, content),
			el(".dhx_span-spans", contentSpans),
			el(".dhx_grid_selection", { _ref: "selection" }, [selection, resizedLine])
		]);
}

function getContentHeight(renderConfig: IRendererConfig, isSticky: boolean, wrapperSizes: { width: number, height: number }): number {
	let contentHeight = wrapperSizes.height - BORDERS;
	contentHeight = isSticky ? contentHeight : contentHeight - renderConfig.headerHeight;


	const isFooter = renderConfig.$footer;

	// [dirty] refactoring needed
	const lessByHeight = renderConfig.$totalHeight + BORDERS < wrapperSizes.height;
	if (renderConfig.splitAt && lessByHeight) {
		return renderConfig.$totalHeight + renderConfig.footerHeight + renderConfig.headerHeight + getScrollbarWidth();
	}

	// [dirty]
	return contentHeight = isFooter ?
		isSticky ? contentHeight : contentHeight - renderConfig.footerHeight
		: contentHeight;
}